import { Box, Button, CircularProgress, Container, Grid, Typography } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import { useCallback, useContext, useEffect, useState } from "react";
import { SuccessAlertContext } from "App";
import { checkAccessTokenExpired } from "util/auth";
import Notify from "config/notify";
import ERROR_MESSAGE from "constants/message";
import useMstMain from "../hooks/useMstMain";
import { MstOption } from "../types";
import { SelectForm } from "../components/Common";
import CsvModals from "../components/CsvModals";

/**
 * 汎用マスタメンテナンス
 * returns JSX.Element
 */
function MstMain() {
  const {showAlert} = useContext(SuccessAlertContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const {
    mstOptions,
    selectedIndex,
    setSelectedIndex,
    getMstOptions,
    handleDownload,
    isOptionsLoading,
    isDownloading,
  } = useMstMain(showAlert);
  const [isOpenUpload, setIsOpenUpload] = useState(false);

  const getSelectedMst = useCallback<() => MstOption | undefined>(() => selectedIndex !== undefined ? mstOptions[selectedIndex] : undefined, [mstOptions, selectedIndex]);

  useEffect(() => {
    setTimeout(() => {
      (async () => {
        const userType = localStorage.getItem("user_type");
        if (!(userType === "C0001")) {
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: "本部ユーザーでログインしてください。再度ログインしてください。",
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
        } else if (checkAccessTokenExpired()) {
          // 期限切れなら
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: ERROR_MESSAGE.UNAUTHORIZED.MESSAGE,
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
        } else {
          getMstOptions();
        }
      })();
    }, 0);
  }, []);

  // ローディングの統一
  useEffect(() => {
    setIsLoading(isDownloading || isOptionsLoading || isUploading);
  }, [isDownloading, isOptionsLoading, isUploading]);

  return (
    <>
      <Box component="header" sx={{ display: "flex", alignItems: "center", px: 2, py: 4 }}>
        <Button
          startIcon={<ArrowBackIosNewIcon />}
          onClick={() => window.history.back()}
          sx={{
            color: "#000",
            textTransform: "none",
            border: "1px solid #ffa500",
            borderRadius: 1,
            mr: 2,
            minWidth: "6rem",
          }}
        >
          戻る
        </Button>
        <Grid container display="flex" flexGrow={1} alignItems="center" justifyContent="center">
          {/* <UploadIcon sx={{ fontSize: "2rem", color: "text.secondary", mr: 2 }} /> */}
          <Typography
            variant="h5"
            component="h1"
            color="text.secondary"
            sx={{
              textAlign: "center",
              m1: 2,
              fontWeight: 700,
              fontSize: "1.8rem",
            }}
          >
            汎用マスタメンテナンス
          </Typography>
        </Grid>
      </Box>

      <Container maxWidth="md">
        <Box mb={2} display="flex" alignItems="center" justifyContent="center" />

        <Box sx={{ p: 4, borderRadius: 2, boxShadow: 1, mb: 2, maxWidth: 800, mx: "auto" }}>
          <Grid container justifyContent="space-between" mb={2}>
            <Typography variant="subtitle1" color="text.secondary" fontWeight={700} gutterBottom>
              対象のマスタを指定して閲覧、更新します
            </Typography>
          </Grid>
          {/* Date Area */}
            <Grid container alignItems="center" justifyContent="center" spacing={1} mb={2}>
              <SelectForm
                id="mst"
                label="対象マスタを選択"
                optionList={mstOptions.map((mo) => ({dispName: mo.displayName, value: mo.tableName}))}
                value={getSelectedMst()?.tableName ?? ""}
                setValue={(val: string) => setSelectedIndex(() => mstOptions.findIndex((mo) => mo.tableName === val))}
              />
            </Grid>
            { getSelectedMst()?.lastUpdate &&
              <Grid container alignItems="left" justifyContent="left" px={15} spacing={-5}>
                <Typography variant="caption" color="text.secondary">
                  ※ 最終更新日： {getSelectedMst()?.lastUpdate ?? '-'}
                </Typography>
              </Grid>
            }

          <Grid
            item
            xs={12}
            sx={{
              textAlign: "right",
              mt: "2",
            }}
          >
            <Button
              variant="contained"
              disabled={isDownloading}
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{
                bgcolor: "#ffa500",
                color: "#000000",
                ":hover": { bgcolor: "#e07b00" },
                textTransform: "none",
                mr: 1
              }}
            >
              {isDownloading ? "CSVダウンロード中" : "CSVダウンロード"}
            </Button>
            <Button
              variant="contained"
              disabled={isLoading || selectedIndex === undefined}
              startIcon={<UploadIcon />}
              onClick={()=> setIsOpenUpload(true)}
              sx={{
                bgcolor: "#ffa500",
                color: "#000000",
                ":hover": { bgcolor: "#e07b00" },
                textTransform: "none",
              }}
            >
              CSVアップロード
            </Button>
          </Grid>
        </Box>
      </Container>
      <CsvModals
        tableName={getSelectedMst()?.tableName}
        isUploading={isUploading}
        setIsUploading={setIsUploading}
        isOpenCsvUpload={isOpenUpload}
        setIsOpenCsvUpload={setIsOpenUpload}
        getMstOptions={getMstOptions}
      />
      {isLoading && ( // マスク
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.2)",
            zIndex: (theme) => theme.zIndex.modal + 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </>
  );
}

export default MstMain;
